import moment from 'moment-timezone';
import systemTimeOffsetStore from 'client/generic/SystemTimeOffset/SystemTimeOffset.store';

export type DueDateResult = {
  text: string;
  isPastDue: boolean;
  color: 'warning' | 'negative' | 'neutral';
};

function getMinutesFromDuration(duration: moment.Duration): number {
  const milliseconds = duration.asMilliseconds();
  const seconds = milliseconds / 1000;
  return Math.round(seconds / 60);
}

export function convertDateAndTimeToUnixTimestamp({
  date,
  time
}: {
  date: string;
  time: string;
}): number {
  return convertDateAndTimeToMomentObject({date, time}).valueOf();
}

export function convertDateAndTimeToMomentObject({
  date,
  time
}: {
  date?: string;
  time?: string;
}): moment.Moment {
  const formattedDate = moment(date).format('YYYY-MM-DD');
  const formattedTime = moment(time, 'h:mm A').format('HH:mm:ss');
  return moment(`${formattedDate} ${formattedTime}`);
}

/**
  We calculate this ourselves because moment's duration object only
  allows us to get hours / minutes as their own thing. Not combined into
  separate values.

  For instance, 100 minutes as a duration w/ .hours gives 1.x, 2/ minutes gives 100,
  we want 1 hour and 40 minutes for display purposes here.
*/
export function humanReadableDurationString(momentDuration: moment.Duration): string {
  const minutes = getMinutesFromDuration(momentDuration);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);

  if (hours) {
    return `${hours} hrs ${remainingMinutes} min`;
  }
  return `${remainingMinutes} min`;
}

type DurationFormatOptions = {style: string; max?: number};

const durationFormats: Record<
  string,
  (duration: moment.Duration, options: DurationFormatOptions) => string
> = {
  minutes(duration, options): string {
    let mins = duration.asMinutes();
    if (options.max && mins >= options.max) {
      return `${options.max}:00s`;
    }
    let [minutes, fractional] = mins
      .toString()
      .split('.')
      .map((n) => {
        const parsed = parseInt(n, 10);
        return Number.isNaN(parsed) ? 0 : parsed;
      });
    let seconds = fractional ? Math.round(parseFloat(`.${fractional}`) * 60) : 0;
    /**
     * Since we're rounding up milliseconds, it's possible seconds will be 60.
     * Rather than rendering: "01:60s" add to the minutes and set the seconds to 0.
     */
    if (seconds === 60) {
      minutes += 1;
      seconds = 0;
    }
    return `${minutes < 10 ? `0${minutes}` : minutes}:${seconds < 10 ? `0${seconds}` : seconds}s`;
  },
  'hh:mm:ss': function (duration): string {
    const clone = duration.clone();
    const hours = Math.floor(clone.asHours());
    clone.subtract(hours, 'hours');
    const minutes = Math.floor(clone.asMinutes());
    clone.subtract(minutes, 'minutes');
    const seconds = Math.floor(clone.asSeconds());
    return [hours, minutes, seconds].map((unit) => `${unit}`.padStart(2, '0')).join(':');
  }
};

export function renderDuration(
  duration: moment.Duration,
  options: DurationFormatOptions = {style: 'minutes'}
): string {
  const formatter = durationFormats[options.style];
  if (!formatter) {
    throw new Error('unknown style value passed to renderDuration');
  }
  return formatter(duration, options);
}

/**
 * "Renders" a moment object to our shared output style.
 *
 * Optionally accepts a `style` parameter sourced from `format`
 */
const formats: Record<string, string> = {
  date: 'MM/DD/YYYY',
  dateTime: 'MM/DD/YYYY, h:mm a z',
  dateFormal: 'MMM Do[,] YYYY',
  dateFormalNoDateSuffix: 'MMM D[,] YYYY',
  dateFormalShort: 'MMM Do',
  dateFormalShortWithFullMonth: 'MMMM Do',
  /**
   * Convert the moment instance to the format accepted by input[type='date'].value
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/date#Value
   */
  dom: 'YYYY-MM-DD',
  time: 'h:mm a z'
};

export function renderMoment(
  m: moment.MomentInput,
  style: string = 'dateTime',
  tz: string = moment.tz.guess()
): string {
  const format = formats[style];
  if (!format) {
    throw new Error('unknown style value passed to renderMoment');
  }
  return moment(m).tz(tz).format(format);
}

/**
 * Takes a timestamp and an optional string to be prepended before the date string.
 */
export function renderTimeFromNow(timestamp: moment.MomentInput, prependedString = ''): string {
  const dateString = moment(timestamp).calendar(null, {
    sameDay: '[today]',
    lastDay: '[yesterday]',
    lastWeek: 'll',
    sameElse: 'll'
  });
  const currentTime = systemTimeOffsetStore.getCurrentTime();
  let modifiedString: string;
  if (currentTime.diff(timestamp, 'seconds') < 60) {
    modifiedString = 'just now';
  } else if (currentTime.diff(timestamp, 'minutes') < 10) {
    modifiedString = moment(timestamp).fromNow();
  } else {
    modifiedString = dateString;
  }

  if (prependedString) {
    return `${prependedString} ${modifiedString}`;
  }
  return modifiedString.charAt(0).toUpperCase() + modifiedString.slice(1);
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n !== 1 ? 's' : ''}`;
}

/**
 * Calculates a human-readable due date label and text color based on the difference between the given date and the current time.
 * Colors:
 *   - 'warning'  — due within 59 minutes
 *   - 'negative' — past due
 *   - 'neutral'  — default state
 *
 * @param dueDate - ISO 8601 string, e.g. '2025-06-28T05:59:00Z'
 */
export function calculateDueDate(dueDate: string): DueDateResult {
  const now = systemTimeOffsetStore.getCurrentTime();
  const due = moment(dueDate);
  const diffMinutes = due.diff(now, 'minutes');
  const isPastDue = diffMinutes < 0;

  let text = `Due ${due.format('dddd')}`;
  let color: DueDateResult['color'] = 'neutral';

  if (isPastDue) {
    const mins = Math.abs(diffMinutes);
    const hours = Math.abs(due.diff(now, 'hours'));
    const days = Math.abs(due.diff(now, 'days'));
    const weeks = Math.abs(due.diff(now, 'weeks'));
    const months = Math.abs(due.diff(now, 'months'));

    color = 'negative';

    if (hours >= 24 && hours < 48) {
      text = 'Due yesterday';
    } else {
      let amount: number;
      let unit: string;
      if (mins < 60) {
        amount = mins;
        unit = 'minute';
      } else if (hours < 24) {
        amount = hours;
        unit = 'hour';
      } else if (days < 7) {
        amount = days;
        unit = 'day';
      } else if (days < 30) {
        amount = weeks;
        unit = 'week';
      } else {
        amount = months;
        unit = 'month';
      }
      text = `Due ${plural(amount, unit)} ago`;
    }

    return {isPastDue, color, text};
  }

  if (diffMinutes < 60) {
    text = `Due in ${plural(diffMinutes, 'minute')}`;
    color = 'warning';
  } else if (diffMinutes < 240) {
    text = `Due in ${plural(due.diff(now, 'hours'), 'hour')}`;
  } else if (due.isSame(now, 'day')) {
    text = 'Due today';
  } else if (due.isSame(now.clone().add(1, 'day'), 'day')) {
    text = 'Due tomorrow';
  } else if (due.diff(now, 'days') < 7) {
    text = `Due in ${plural(due.diff(now, 'days'), 'day')}`;
  } else {
    const nextWeekStart = now.clone().add(1, 'week').startOf('week');
    const nextWeekEnd = nextWeekStart.clone().endOf('week');
    if (due.isBetween(nextWeekStart, nextWeekEnd, 'day', '[]')) {
      text = 'Due next week';
    } else {
      text = `Due ${due.format('MMMM Do')}`;
    }
  }

  return {isPastDue, color, text};
}
